import type { ContentObjectSubmitFn } from '../../../form/ContentObjectForm/ContentObjectForm';

import { use, useCallback, useId, useMemo, useState } from 'react';
import TagManager from 'react-gtm-module';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { batchPatchContentObject } from '../../../lib/flotiq-client';
import { handleCTOErrors } from '../../../lib/flotiq-client/api-helpers';
import { parseResponseErrors } from '../../../lib/flotiq-client/response-errors';
import { getTestProps } from '../../../lib/helpers';

import CancelButton from '../../../components/Button/predefined/CancelButton/CancelButton';
import SaveButton from '../../../components/Button/predefined/SaveButton/SaveButton';
import Loader from '../../../components/Loader/Loader';

import { useContentType } from '../../../hooks/api';
import useApiErrorsToast from '../../../hooks/api/useApiErrorsToast';
import useSelectedSpace from '../../../hooks/useSelectedSpace';
import useToken from '../../../hooks/useToken';

import { ModalInstanceContext } from '../../../contexts/ModalContext';

import ContentObjectForm from '../../../form/ContentObjectForm/ContentObjectForm';

interface MassEditModalFormProps {
  /**
   * Content object type name
   */
  contentTypeName?: string;
  /**
   * Content object ids
   */
  ids: string[];
  /**
   * Mass edit modal test id
   */
  testId?: string;
}

const MassEditModalForm = ({
  contentTypeName,
  ids,
  testId = '',
}: MassEditModalFormProps) => {
  const jwt = useToken();
  const { space } = useSelectedSpace();
  const formId = useId();
  const { t } = useTranslation();
  const modalInstance = use(ModalInstanceContext);
  const [isSaving, setIsSaving] = useState(false);
  const [overriddenFields, setOverriddenFields] = useState({});

  const onClose = useCallback(() => modalInstance.resolve?.(), [modalInstance]);

  const {
    entity: contentType,
    errors: contentTypeErrors,
    isLoading: contentTypeLoading,
  } = useContentType(contentTypeName);

  useApiErrorsToast(contentTypeErrors);

  const batchPatchObjects = useCallback<ContentObjectSubmitFn>(
    async (values) => {
      const valuesToOverride: Record<string, unknown> = {};
      Object.keys(overriddenFields).forEach((key) => {
        if (overriddenFields[key as keyof typeof overriddenFields]) {
          valuesToOverride[key] = values[key];
        }
      });
      const batchValues: Record<string, unknown>[] = [];
      ids.forEach((_id) => {
        batchValues.push({
          id: _id,
          ...valuesToOverride,
        });
      });

      try {
        const { status, body, ok } = await batchPatchContentObject(
          jwt,
          space,
          contentTypeName,
          batchValues,
        );
        if (!ok) {
          throw parseResponseErrors(body, status);
        }
        toast.success(
          t('ContentForm.SavedMultiple', {
            count: body.batch_success_count,
          }),
        );

        TagManager.dataLayer({
          dataLayer: {
            event: 'mass_co_change',
          },
        });

        setIsSaving(false);
        onClose();

        return [null, {}, false];
      } catch (error) {
        setIsSaving(false);
        const [contentObject, errors, hasErrors] = handleCTOErrors(
          error,
          valuesToOverride,
          t,
        );
        if (!hasErrors) onClose();
        return [contentObject, errors, hasErrors];
      }
    },
    [contentTypeName, ids, jwt, onClose, overriddenFields, space, t],
  );

  const updateObject = useCallback<ContentObjectSubmitFn>(
    async (values) => {
      setIsSaving(true);
      return batchPatchObjects(values);
    },
    [batchPatchObjects],
  );

  const updateOverriddenFields = useCallback(
    (checked: boolean, key: string) =>
      setOverriddenFields((prev) => ({ ...prev, [key]: checked })),
    [],
  );

  const modalContent = useMemo(() => {
    if (contentTypeLoading)
      return (
        <div className="w-full h-full flex items-center justify-center">
          <Loader size="big" type="spinner-grid" />
        </div>
      );

    return (
      <ContentObjectForm
        contentType={contentType}
        onSubmit={updateObject}
        disabled={isSaving}
        formId={formId}
        testId={testId}
        isPatchable={true}
        isEditing={true}
        overriddenFields={overriddenFields}
        setOverriddenFields={updateOverriddenFields}
        editedCount={ids?.length}
      />
    );
  }, [
    contentType,
    contentTypeLoading,
    formId,
    ids?.length,
    isSaving,
    overriddenFields,
    testId,
    updateObject,
    updateOverriddenFields,
  ]);

  return (
    <>
      {modalContent}
      <div
        className="w-full absolute left-0 bottom-0 flex items-center justify-center p-3 space-x-5
        border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900"
      >
        <CancelButton
          onClick={onClose}
          disabled={isSaving}
          {...getTestProps(testId, 'cancel', 'testId')}
        />
        <SaveButton
          form={formId}
          isSaving={isSaving}
          {...getTestProps(testId, 'save', 'testId')}
        />
      </div>
    </>
  );
};

export default MassEditModalForm;
